// src/components/ProtocolTester.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Play, Sparkles } from "lucide-react";

import {
  useGetProtocolsQuery,
  useGetUnreviewedPacketsQuery,
  useGetRuntimeProtocolsQuery,
  useTestProtocolMutation,
  useTestRuntimeProtocolMutation,
} from "../../redux/protocol/protocolApi";

export default function ProtocolTester() {
  const theme = useTheme();
  const [form, setForm] = useState({
    mode: "runtime",
    protocolId: "",
    runtimeProtocol: "",
    rawHex: "",
    imei: "",
  });
  const [result, setResult] = useState(null);

  const { data: protocolsData, error: protocolsQueryError } = useGetProtocolsQuery();
  const { data: runtimeData, error: runtimeQueryError } = useGetRuntimeProtocolsQuery();
  const { data: recentPacketsData } = useGetUnreviewedPacketsQuery({ page: 1, limit: 20 });
  const [testProtocol, { isLoading: isTesting, error }] = useTestProtocolMutation();
  const [testRuntimeProtocol, { isLoading: isRuntimeTesting, error: runtimeError }] =
    useTestRuntimeProtocolMutation();

  const protocols = protocolsData?.data || [];
  const runtimeProtocols = runtimeData?.data?.runtime || [];
  const runtimeFallbackFromProtocols = protocols
    .filter((p) => p?.runtimeCompatible && p?.runtimeBoundProtocol)
    .map((p) => ({
      key: p.runtimeBoundProtocol,
      displayName: p.runtimeDisplayName || p.name || p.protocolId || p.runtimeBoundProtocol,
      port: p.runtimePort ?? null,
    }));
  const runtimeOptions = runtimeProtocols.length ? runtimeProtocols : runtimeFallbackFromProtocols;

  const defaultSamplePackets = useMemo(
    () => [
      {
        name: "GT06 Login",
        hex: "78780d0103593390754169920001d9dc0d0a",
        description: "Login packet with IMEI",
      },
      {
        name: "GT06 Location",
        hex: "78781f12140c1e0b3b1eca0270b3fd09d4b8ab00d6070000fbee0a326e140d0a",
        description: "GPS location data",
      },
      {
        name: "GT06 Heartbeat",
        hex: "787808134b040300010050d90d0a",
        description: "Heartbeat/status packet",
      },
    ],
    []
  );
  const protocolSamples = useMemo(
    () => ({
      concox: defaultSamplePackets,
      gt06: defaultSamplePackets,
      tk003: defaultSamplePackets,
      g17h: defaultSamplePackets,
      g17s: defaultSamplePackets,
      g17h_mic: defaultSamplePackets,
      pt20_6000: defaultSamplePackets,
      pt20_10000: defaultSamplePackets,
      ps25: defaultSamplePackets,
      v5: defaultSamplePackets,
      xyz1: defaultSamplePackets,
      idcard: defaultSamplePackets,
      id0024g: defaultSamplePackets,
      teltonika_fmb920: [
        {
          name: "Teltonika Login (IMEI ASCII)",
          hex: "000f333536333037303432343431303133",
          description: "TCP login packet (2-byte length + 15-byte IMEI ASCII)",
        },
      ],
      topflytech_pioneerx: [
        {
          name: "Topflytech (Need device sample)",
          hex: "",
          description: "Use latest captured packet or live packet from logs.",
        },
      ],
    }),
    [defaultSamplePackets]
  );
  const selectedProtocolKey =
    form.mode === "runtime" ? form.runtimeProtocol : (form.protocolId || "").toLowerCase();
  const samplePackets = protocolSamples[selectedProtocolKey] || defaultSamplePackets;
  const recentPackets = recentPacketsData?.data || [];

  const cleanHex = (v) => (v || "").replace(/\s/g, "");

  const handleTest = async (e) => {
    e.preventDefault();
    setResult(null);

    try {
      if (form.mode === "runtime") {
        const res = await testRuntimeProtocol({
          runtimeProtocol: form.runtimeProtocol,
          protocolId: form.protocolId || undefined,
          rawHex: cleanHex(form.rawHex),
          imei: form.imei || undefined,
        }).unwrap();
        setResult({ mode: "runtime", ...(res.data || {}) });
      } else {
        const res = await testProtocol({
          protocolId: form.protocolId,
          rawHex: cleanHex(form.rawHex),
        }).unwrap();
        setResult({ mode: "db", ...(res.data || {}) });
      }
    } catch (err) {
      console.error("Test failed:", err);
    }
  };

  const handleUseLatestPacket = () => {
    if (!recentPackets.length) return;
    const selectedId = (form.protocolId || form.runtimeProtocol || "").toLowerCase();
    const best =
      recentPackets.find((p) => String(p?.matchedProtocolId || "").toLowerCase() === selectedId) ||
      recentPackets[0];
    if (best?.rawHex) {
      setForm((p) => ({ ...p, rawHex: cleanHex(best.rawHex) }));
    }
  };

  const statusTone = result?.matched
    ? {
        bg: alpha(theme.palette.success.main, 0.12),
        border: alpha(theme.palette.success.main, 0.3),
        fg: theme.palette.success.dark,
        label: "Matched",
      }
    : result
    ? {
        bg: alpha(theme.palette.warning.main, 0.14),
        border: alpha(theme.palette.warning.main, 0.35),
        fg: theme.palette.warning.dark,
        label: "No Match",
      }
    : null;

  const InfoTile = ({ label, value, code }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.text.primary, 0.02),
        borderColor: alpha(theme.palette.divider, 0.9),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      {code ? (
        <Typography component="code" sx={{ display: "block", mt: 0.3, fontWeight: 900 }}>
          {value}
        </Typography>
      ) : (
        <Typography sx={{ mt: 0.3, fontWeight: 900 }}>{value}</Typography>
      )}
    </Paper>
  );

  return (
    <Box>
      {/* Tester card */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack spacing={0.8} sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Protocol Tester</Typography>
          <Typography variant="body2" color="text.secondary">
            Manual setup check: paste one complete raw packet copied from TCP logs or the tracker document examples.
            This tester does not connect, command, or disturb any live tracker.
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleTest}>
          <Stack spacing={1.5}>
            {!!protocolsQueryError && (
              <Alert severity="warning">
                Protocol list load failed. Check token/role access and backend route
                <code> /api/protocol-management/protocols</code>.
              </Alert>
            )}
            {!!runtimeQueryError && (
              <Alert severity="warning">
                Runtime protocol list failed. Check role access for
                <code> /api/protocol-management/runtime/protocols</code>.
              </Alert>
            )}
            <TextField
              select
              label="Test Mode"
              value={form.mode}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  mode: e.target.value,
                  protocolId: "",
                  runtimeProtocol: "",
                }))
              }
              size="small"
              required
            >
              <MenuItem value="runtime">Existing live protocol check (current adapters)</MenuItem>
              <MenuItem value="db">My saved configuration - safe test only</MenuItem>
            </TextField>

            {form.mode === "runtime" ? (
              <TextField
                select
                label="Select Runtime Protocol"
                value={form.runtimeProtocol}
                onChange={(e) => setForm((p) => ({ ...p, runtimeProtocol: e.target.value }))}
                size="small"
                helperText={
                  runtimeOptions.length
                    ? "Loaded from runtime registry"
                    : "No runtime protocols returned. Restart backend and check role access."
                }
                required
              >
                <MenuItem value="">-- Select --</MenuItem>
                {runtimeOptions.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.displayName || p.name || p.key}
                    {p.port ? ` (:${p.port})` : ""}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
            <TextField
              select
              label="Select Protocol"
              value={form.protocolId}
              onChange={(e) => setForm((p) => ({ ...p, protocolId: e.target.value }))}
              size="small"
              required
            >
              <MenuItem value="">-- Select --</MenuItem>
                {protocols.map((p) => (
                  <MenuItem key={p._id} value={p.protocolId}>
                  {p.name} ({p.protocolId}){p.runtimePort ? `  :${p.runtimePort}` : ""}
                  </MenuItem>
                ))}
            </TextField>
            )}

            <TextField
              label="Complete packet in hexadecimal"
              value={form.rawHex}
              onChange={(e) => setForm((p) => ({ ...p, rawHex: cleanHex(e.target.value) }))}
              size="small"
              required
              multiline
              minRows={3}
              placeholder="78780d0103..."
              inputProps={{ style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" } }}
              helperText="Paste only 0-9 and A-F. Spaces are removed automatically. Example: 7878...0d0a"
            />
            <Alert severity="info">
              Hex lane ke easy tareeke: (1) Sample Packets se load karo, (2) <b>Use Latest Captured Packet</b>
              button dabao, (3) live TCP logs ka raw packet paste karo.
            </Alert>

            {form.mode === "runtime" && (
              <TextField
                label="IMEI (Optional)"
                value={form.imei}
                onChange={(e) => setForm((p) => ({ ...p, imei: e.target.value }))}
                size="small"
                placeholder="8697270XXXXXXXX"
              />
            )}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleUseLatestPacket}
                disabled={!recentPackets.length}
                sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
              >
                Use Latest Captured Packet
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isTesting || isRuntimeTesting}
                startIcon={<Play size={16} />}
                sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none" }}
              >
                {isTesting || isRuntimeTesting ? "Testing..." : "Test Protocol"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Sample packets */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 900 }}>Sample Packets</Typography>
            <Typography variant="body2" color="text.secondary">
              Click a sample to load hex quickly.
            </Typography>
          </Box>
          <Chip size="small" label={`${samplePackets.length} samples`} variant="outlined" sx={{ fontWeight: 900 }} />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 1.2,
          }}
        >
          {samplePackets.map((s, i) => (
            <Paper
              key={i}
              variant="outlined"
              onClick={() => s.hex && setForm((p) => ({ ...p, rawHex: s.hex }))}
              sx={{
                p: 1.5,
                borderRadius: 3,
                cursor: s.hex ? "pointer" : "not-allowed",
                opacity: s.hex ? 1 : 0.7,
                transition: "0.18s",
                borderColor: alpha(theme.palette.divider, 0.9),
                "&:hover": {
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                },
              }}
            >
              <Typography sx={{ fontWeight: 900, mb: 0.5 }}>{s.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {s.description}
              </Typography>

              <Box
                component="code"
                sx={{
                  display: "block",
                  p: 1,
                  borderRadius: 2,
                  fontSize: 12,
                  wordBreak: "break-all",
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                }}
              >
                {s.hex}
              </Box>
            </Paper>
          ))}
        </Box>
      </Paper>

      {/* Error */}
      {(error || runtimeError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {runtimeError?.data?.error || error?.data?.error || "Test failed"}
        </Alert>
      )}

      {/* Result */}
      {result && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Typography sx={{ fontWeight: 900 }}>Test Result</Typography>
            <Chip
              size="small"
              label={statusTone?.label || "Result"}
              sx={{
                fontWeight: 900,
                borderRadius: 999,
                bgcolor: statusTone?.bg,
                color: statusTone?.fg,
                border: `1px solid ${statusTone?.border}`,
              }}
            />
          </Stack>

          {/* match banner */}
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              mb: 2,
              bgcolor: statusTone?.bg,
              borderColor: statusTone?.border,
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 18, color: statusTone?.fg }}>
              {result.matched ? "✅ Protocol Matched" : "⚠️ No Match"}
            </Typography>
            {result.matchResult?.score !== undefined && (
              <Typography sx={{ mt: 0.5, fontWeight: 900, color: statusTone?.fg }}>
                Score: {result.matchResult.score} / 100
              </Typography>
            )}
          </Paper>

          {/* runtime result */}
          {result.mode === "runtime" && (
            <>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Runtime Parse Output</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.2,
                  mb: 2,
                }}
              >
                <InfoTile label="Runtime Protocol" value={result.runtimeProtocol || "N/A"} />
                <InfoTile label="Packet Type" value={result.parsed?.type || "INVALID"} />
                <InfoTile label="IMEI" value={result.parsed?.imei || "N/A"} />
                <InfoTile label="ACK Hex" value={result.ackHex || "N/A"} code />
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 2,
                  fontSize: 12,
                  overflow: "auto",
                  bgcolor: alpha(theme.palette.text.primary, 0.04),
                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(result.parsed || {}, null, 2)}
              </Box>
            </>
          )}

          {/* saved configuration test result */}
          {result.mode !== "runtime" && result.dynamicTest && (
            <>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Your Configuration Result</Typography>
              <Alert severity={result.dynamicTest.ok ? "success" : "warning"} sx={{ mb: 1.5 }}>
                {result.dynamicTest.safety}
              </Alert>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.2,
                  mb: 1.5,
                }}
              >
                <InfoTile label="Packet length" value={`${result.dynamicTest.packetBytes || 0} bytes`} />
                <InfoTile label="Decoded tracker ID" value={result.dynamicTest.imei || "Not decoded"} />
                <InfoTile label="Start bytes found" value={result.dynamicTest.framing?.header || "No"} code />
                <InfoTile
                  label="CRC check"
                  value={result.dynamicTest.framing?.crc?.checked
                    ? result.dynamicTest.framing.crc.valid ? "✓ Correct" : "✗ Does not match"
                    : "Not required / not checked"}
                />
              </Box>
              {!!Object.keys(result.dynamicTest.fields || {}).length && (
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 900, mb: 1 }}>Decoded fields</Typography>
                  {Object.entries(result.dynamicTest.fields).map(([name, field]) => (
                    <Stack key={name} direction="row" justifyContent="space-between" sx={{ py: 0.5, gap: 1 }}>
                      <Typography>{name}</Typography>
                      <Typography sx={{ fontWeight: 900 }}>
                        {String(field.value)}{field.unit ? ` ${field.unit}` : ""}
                        {field.raw !== field.value ? ` (raw: ${field.raw})` : ""}
                      </Typography>
                    </Stack>
                  ))}
                </Paper>
              )}
              {!!result.dynamicTest.errors?.length && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {result.dynamicTest.errors.map((message, index) => <div key={index}>{message}</div>)}
                </Alert>
              )}
              {!!result.dynamicTest.warnings?.length && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {result.dynamicTest.warnings.map((message, index) => <div key={index}>{message}</div>)}
                </Alert>
              )}
            </>
          )}

          {/* legacy matcher result */}
          {result.mode !== "runtime" && result.fingerprint && (
            <>
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Detected Fingerprint</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.2,
                  mb: 2,
                }}
              >
                <InfoTile label="Header" value={result.fingerprint.header || "N/A"} code />
                <InfoTile label="Pattern Type" value={result.fingerprint.patternType || "unknown"} />
                <InfoTile label="Has CRC" value={result.fingerprint.hasCRC ? "Yes" : "No"} />
                <InfoTile label="Stop Bit" value={result.fingerprint.stopBit || "N/A"} code />
              </Box>
            </>
          )}

          {/* match details */}
          {result.mode !== "runtime" && result.matchResult?.details && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontWeight: 900, mb: 1 }}>Match Details</Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.2,
                }}
              >
                <InfoTile
                  label="Header Match"
                  value={result.matchResult.details.headerMatch ? "✓ Yes" : "✗ No"}
                />
                <InfoTile
                  label="Stop Bit Match"
                  value={result.matchResult.details.stopBitMatch ? "✓ Yes" : "✗ No"}
                />
                <InfoTile
                  label="CRC Match"
                  value={result.matchResult.details.crcMatch ? "✓ Yes" : "✗ No"}
                />
                <InfoTile
                  label="Length Match"
                  value={result.matchResult.details.lengthMatch ? "✓ Yes" : "✗ No"}
                />
                <InfoTile
                  label="IMEI Decoded"
                  value={
                    result.matchResult.details.imeiDecoded
                      ? `✓ ${result.matchResult.details.decodedIMEI}`
                      : "✗ No"
                  }
                />
              </Box>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
